import { useState } from "react";
import { toast } from "sonner";
import { IoSwapHorizontalOutline, IoCalendarOutline, IoWarningOutline } from "react-icons/io5";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { DateInput } from "@/components/DateInput";
import api from "@/api";
import { useI18n } from "@/i18n";
import { tapSuccess, tapError } from "@/utils/haptics";

export const ReservationDrawer = ({ open, onOpenChange, product, onSuccess }) => {
  const { t } = useI18n();
  const [qty, setQty] = useState(1);
  const [pickupDate, setPickupDate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const available = product?.quantity || 0;

  const submit = async () => {
    const q = Number(qty);
    if (!q || q < 1) {
      toast.error("La quantità deve essere almeno 1");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/products/${product.id}/reserve`, {
        quantity: q,
        pickup_date: pickupDate || "",
        notes,
      });
      tapSuccess();
      toast.success(t("reservation_created"));
      onOpenChange(false);
      setQty(1);
      setPickupDate("");
      setNotes("");
      onSuccess?.();
    } catch (e) {
      tapError();
      toast.error(e?.response?.data?.detail || "Errore prenotazione");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent data-testid="reserve-drawer">
        <DrawerHeader>
          <DrawerTitle className="tf-drawer-title" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {t("reserve")}: {product?.name}
            </span>
            <IoSwapHorizontalOutline size={22} style={{ flexShrink: 0 }} />
          </DrawerTitle>
        </DrawerHeader>
        <div className="tf-drawer-body">
          {available > 0 ? (
            <p className="tf-muted" style={{ marginTop: 0 }}>
              {t("quantity_available")}: <strong>{available}</strong>
            </p>
          ) : (
            <div
              className="tf-warning-banner"
              data-testid="reserve-no-stock"
              style={{
                marginTop: 0,
                padding: "10px 12px",
                borderRadius: 10,
                background: "var(--tf-yellow-soft, #FEF3C7)",
                color: "#7C4A03",
                display: "flex",
                alignItems: "center",
                gap: 8,
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              <IoWarningOutline size={18} />
              Attualmente 0 pz — la richiesta sarà inviata come &laquo;da rifornire&raquo;.
            </div>
          )}

          <div className="tf-field">
            <label className="tf-label">{t("reserved_quantity")} *</label>
            <input
              data-testid="reserve-qty"
              className="tf-input"
              type="number"
              min={1}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              inputMode="numeric"
            />
          </div>

          <div className="tf-field">
            <label className="tf-label">{t("pickup_date")}</label>
            <DateInput data-testid="reserve-pickup" value={pickupDate} onChange={setPickupDate} />
          </div>

          <div className="tf-field">
            <label className="tf-label">{t("notes")}</label>
            <textarea
              data-testid="reserve-notes"
              className="tf-textarea"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <button
            data-testid="reserve-confirm"
            className="tf-btn-primary"
            disabled={saving}
            onClick={submit}
          >
            <IoCalendarOutline size={18} />
            {saving ? t("saving") : t("reserve")}
          </button>
        </div>
      </DrawerContent>
    </Drawer>
  );
};
